<template>
  <Modal
    @close="$emit('close')"
  >
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('HeadContract.DelaysSchedule.RegisterValueModal.title') }} {{ delay.code }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col overflow-y-auto w-full">
        <p class="font-semibold text-lg">
          Delay
        </p>

        <p class="font-semibold">
          Description
        </p>

        <p>
          {{ delay.code }} - {{ delay.description }}
        </p>

        <p class="font-semibold mt-2">
          Notes
        </p>

        <div
          v-if="delay.notes"
        >
          {{ delay.notes }}
        </div>

        <form
          action=""
          class="flex flex-col space-y-4 mt-4"
          @submit.prevent=""
        >
          <label>
            {{ $t('HeadContract.DelaysSchedule.RegisterValueModal.label.dateReceived') }}

            <AppInput
              v-model="pricingForm.form.date_received"
              name="date_received"
              type="date"
              class="form-input"
              :error="pricingForm.getErrors('date_received')"
            />
          </label>

          <label>
            {{ $t('HeadContract.DelaysSchedule.RegisterValueModal.label.to') }}

            <AppInput
              v-model="pricingForm.form.to"
              name="to"
              type="text"
              class="form-input"
              :error="pricingForm.getErrors('to')"
            />
          </label>

          <label>
            {{ $t('HeadContract.DelaysSchedule.RegisterValueModal.label.from') }}

            <AppInput
              v-model="pricingForm.form.from"
              name="from"
              class="form-input"
              type="text"
              :error="pricingForm.getErrors('from')"
            />
          </label>

          <label>
            {{ $t('HeadContract.DelaysSchedule.RegisterValueModal.label.description') }}

            <textarea
              v-model="pricingForm.form.description"
              name="description"
              class="form-input resize-none"
              type="text"
            />

            <span
              v-if="pricingForm.getErrors('description')"
              class="text-red-600 text-sm"
            >{{ pricingForm.getErrors('description') }}</span>
          </label>

          <label class="flex justify-between">
            {{ $t('HeadContract.DelaysSchedule.RegisterValueModal.label.responseRequired') }}

            <input
              v-model="pricingForm.form.response_required"
              name="response_required"
              type="checkbox"
              class="form-checkbox"
              true-value="1"
              false-value="0"
            >
          </label>

          <label class="mt-4">
            {{ $t('HeadContract.DelaysSchedule.RegisterValueModal.label.duration') }} <span class="text-sm text-gray-400">({{ $t('HeadContract.DelaysSchedule.RegisterValueModal.hint.days') }})</span>

            <AppInput
              v-model="pricingForm.form.days"
              type="number"
              name="days"
              class="form-input mt-2"
              :error="pricingForm.getErrors('days')"
            />
          </label>

          <FileUpload
            v-model="pricingForm.form.media"
            multiple
            @change="addFile"
          />

          <div
            v-if="filesUploaded.length"
            class="overflow-x-auto max-w-sm flex"
          >
            <UploadedFileDisplay
              v-for="(file, i) in filesUploaded"
              :key="`${file.fileName}-${i}`"
              :file="file"
              :error="pricingForm.getErrors(`media.${i}`)"
              @delete="removeFile(i)"
            />
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close')"
        >
          {{ $t('HeadContract.DelaysSchedule.RegisterValueModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          data-test="apply-filter"
          @click="savePricingNotice"
        >
          {{ $t('HeadContract.DelaysSchedule.RegisterValueModal.button.save') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import Modal from '@/components/Modal.vue';
import UploadedFileDisplay from '@/components/UploadedFileDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, dateForDateInput, falsey } from '@/helpers';
import { useDelaysStore } from '@/store/delays';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const emit = defineEmits<{
  (e: 'close');
  (e: 'provisioned');
}>();

const props = defineProps<{
  delayId: Id;
}>();

const delaysStore = useDelaysStore();

const delay = computed(() => {
  return delaysStore.models.find(props.delayId);
});

const pricingForm = useFormData({
  days: 0,
  date_received: dateForDateInput(new Date()),
  to: '',
  from: '',
  description: '',
  response_required: 0,
  media: [],
});

const delayValueAction = useStoreApiAction(delaysStore.storeValueNotice);

const savePricingNotice = () => {
  delayValueAction.request(props.delayId, pricingForm.toFormDataObject()).then((data) => {
    emit('provisioned');

    Swal.fire('Delay Days Saved', '', 'success').then(() => {
      emit('close');
    });
  }).catch((error) => {
    pricingForm.setErrors(error.data);
    console.log(error);
  });
};

const { filesUploaded, addFile, removeFile } = useFileUploadForm(undefined, (file, index) => {
  pricingForm.form.media.splice(index, 1);
});
</script>

<style scoped></style>
